import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { generateUsername } from "@/lib/username-generator";

/**
 * Silently assigns a username to the current user if they don't have one.
 * Runs once on mount. Retries up to 5 times on unique constraint collision.
 */
export function useUsername() {
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;

    const assignIfMissing = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const { data: profile } = await supabase
        .from("profiles")
        .select("username")
        .eq("id", session.user.id)
        .single();

      if (profile?.username) return;

      // Try up to 5 times to generate a unique username
      for (let i = 0; i < 5; i++) {
        const username = generateUsername();

        const { error } = await supabase
          .from("profiles")
          .update({ username })
          .eq("id", session.user.id);

        if (!error) return;
        // If unique constraint violation, retry
        if (error.code !== "23505") return;
      }
    };

    assignIfMissing();
  }, []);
}
